import React, { useEffect, useMemo } from 'react';
import { Collapse, Form, FormProps } from 'antd';
import MetricDefinition from '@src/emp-3.0/components/Metrics/IndividualMetric/MetricBuilder/MetricDefinition';
import { cloneDeep } from 'lodash';
import AddEllipsisWithTooltip from '@components/common/EllipsisTooltip/AddEllipsisWithTooltip';
import { EMetricTypes } from '@src/emp-3.0/types';
import { useMetricBuilderStore } from '../store';
import {
  CollapseTitle,
  CustomCollapse,
  Dot,
  FlowNameWrapper,
  LabelWrapper,
} from '@src/emp-3.0/components/Metrics/Flow/FlowForm/styled';
import { MarginWrapper } from '@src/emp-3.0/components/Metrics/IndividualMetric/MetricBuilder/styled';
import MetricEvents from './MetricEvents';
import { useWorkflowStore } from '@src/pages/workflow/store';
import { isViewMode } from '@src/pages/metric-templates/utils';
import MetricInformation from '@src/emp-3.0/components/Metrics/IndividualMetric/MetricBuilder/MetricInformation';
import { RightOutlined } from '@ant-design/icons';
import MetricName from '@src/emp-3.0/components/Metrics/IndividualMetric/MetricBuilder/MetricName';
import { useBuildMetricFormData } from '@src/emp-3.0/hooks';
import RatioMetricFormular from './RatioMetricFormular';

interface IProps extends FormProps {
  metric: any;
  formProps: FormProps;
  mode: string;
}

const MetricTypeOptions = {
  0: {
    value: EMetricTypes.DURATION,
    label: 'Duration',
    description: 'Calculate the time duration between events',
  },
  1: {
    value: EMetricTypes.CONVERSION,
    label: 'Conversion',
    description: 'Calculate the conversion rate and count between events',
  },
  2: {
    value: EMetricTypes.SINGLE_EVENT,
    label: 'Event Aggregation',
    description: 'Calculate the number or values of an event',
  },
  [EMetricTypes.RATIO_METRIC]: {
    value: EMetricTypes.SINGLE_EVENT,
    label: 'Ratio Metric',
  },
};
export const renderItemForViewMode = (form, item, value?: string) => {
  if (item === 'metrics_type') {
    const metricType = form.getFieldValue(item);
    return (
      <span>
        <span style={{ display: 'inline-block', width: '128px' }}>
          &nbsp;{MetricTypeOptions[metricType]?.label}&nbsp;
        </span>
      </span>
    );
  }
  if (item === 'metric_category') {
    return metricCategoryOptions.find((i) => i.value === form.getFieldValue(item))?.label || '';
  }
  if (item === 'withinRange' || item === 'withinUnit') {
    return <div>&nbsp;{form.getFieldValue(item)}&nbsp;</div>;
  }
  if (item === 'description' && !value) {
    return <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontWeight: 400 }}>No Description</div>;
  }
  return (
    <AddEllipsisWithTooltip
      text={`\u00A0${value || form.getFieldValue(item)}\u00A0`}
      dataIndex="0"
    />
  );
};

// When create metric switch metric type, need to keep previous definition data.
let typeDefinitionMap: {} = {};

const metricCategoryOptions = [
  { label: 'User Login', value: 1 },
  { label: 'Subscription', value: 2 },
  { label: 'Custom', value: 3 },
];

const getCollapseHeader = ({ key, mode, metricType }) => {
  const isView = isViewMode(mode);

  const labels = {
    '1': 'Metric Information',
    '2': 'Event Selection',
    '3': 'Configuration',
    '4': 'Details',
  };
  if (metricType === EMetricTypes.RATIO_METRIC) {
    labels['2'] = 'Formula';
  }

  const label = labels?.[key];

  return (
    <LabelWrapper>
      {isView ? (
        <>
          <Dot /> {label}
        </>
      ) : (
        `${key}. ${label}`
      )}
    </LabelWrapper>
  );
};

const MetricForm: React.FC<IProps> = ({ formProps, metric, mode }) => {
  const [internalFormInstance] = Form.useForm();
  const { buildMetricFormData } = useBuildMetricFormData();
  const {
    setActiveSectionKey,
    activeSectionKey,
    initialEvent,
    completeEvent,
    withoutEvent,
    setFormInstance,
    setMetricType,
    setInitialEvent,
    setCompleteEvent,
  } = useMetricBuilderStore();
  const { formMode } = useWorkflowStore();

  const metricFormInitialValues = useMemo(() => buildMetricFormData(), []);

  const isView = isViewMode(mode);

  const form = formProps.form || internalFormInstance;

  const metricType = Form.useWatch('metrics_type', form);

  useEffect(() => {
    setFormInstance(form);
  }, [form]);

  useEffect(() => {
    // reset map
    typeDefinitionMap = {};
    if (metric) {
      const copied = cloneDeep(metric);
      const cleanedData = buildMetricFormData(copied);
      form.setFieldsValue({ ...cleanedData });
      typeDefinitionMap[cleanedData.metrics_type] = cleanedData.definition;
    } else {
      form.setFieldsValue({ ...metricFormInitialValues });
    }
  }, [metric]);

  useEffect(() => {
    if (metricType !== undefined) {
      setMetricType(metricType);
      form.setFieldsValue({ definition: typeDefinitionMap[metricType] });
    }

    if (metric && metricType !== undefined && metricType !== metric.metrics_type) {
      if (metricType === EMetricTypes.SINGLE_EVENT) {
        // clean initial and complete event
        setInitialEvent(null);
        setCompleteEvent(null);
        form.setFieldsValue({
          definition: {
            first_event: undefined,
            second_event: undefined,
            event: undefined,
          },
        });
      }

      if (Array.isArray(activeSectionKey)) {
        setActiveSectionKey(activeSectionKey.concat(['2']));
      } else {
        setActiveSectionKey([activeSectionKey || '', '2']);
      }
    }
  }, [metricType]);

  const handleFormValuesChange = (changedValues, values) => {
    const isTypeChanged = changedValues.metrics_type !== undefined;
    if (isTypeChanged) {
      const type = changedValues.metrics_type;
      if (!typeDefinitionMap[type]) {
        // keep previous events
        const { first_event, second_event, event } = values.definition || {};
        const defaultData = buildMetricFormData(type, {
          definition: {
            first_event,
            second_event,
            event,
          },
        });

        typeDefinitionMap[type] = { ...defaultData.definition };
      }
    } else {
      typeDefinitionMap[values.metrics_type] = values.definition;
    }
    form.setFieldsValue({ definition: typeDefinitionMap[values.metrics_type] });
    formProps?.onValuesChange?.(changedValues, values);
  };

  const CollapseItems = useMemo(() => {
    return [
      {
        key: '1',
        header: <CollapseTitle>{getCollapseHeader({ key: '1', mode: formMode })} </CollapseTitle>,
        children: <MetricInformation form={form} metric={metric} />,
      },
      {
        key: '2',
        header: (
          <CollapseTitle>
            {getCollapseHeader({ key: '2', mode: formMode, metricType })}
          </CollapseTitle>
        ),
        children:
          metricType === EMetricTypes.RATIO_METRIC ? (
            <RatioMetricFormular form={form} isView={isView} metricInfo={metric} />
          ) : (
            <MetricEvents form={form} metricType={metricType} isView={isView} />
          ),
      },
      {
        key: '3',
        header: <CollapseTitle>{getCollapseHeader({ key: '3', mode: formMode })}</CollapseTitle>,
        children: <MetricDefinition form={form} metric={metric} />,
      },
    ];
  }, [metricType, initialEvent, completeEvent, withoutEvent, metric]);

  return (
    <MarginWrapper>
      <Form
        form={form}
        {...formProps}
        onValuesChange={handleFormValuesChange}
        initialValues={metricFormInitialValues}
      >
        <>
          {isView ? null : (
            <FlowNameWrapper>
              <MetricName mode={mode} form={form} />
            </FlowNameWrapper>
          )}
          <div>
            <CustomCollapse
              expandIconPosition={'end'}
              expandIcon={({ isActive }) => (
                <RightOutlined rotate={isActive ? 90 : 0} style={{ color: 'rgba(0,0,0,0.45)' }} />
              )}
              bordered={false}
              ghost
              activeKey={activeSectionKey}
              onChange={(key) => setActiveSectionKey(key)}
            >
              {CollapseItems.map((item, index) => {
                const showBorder = index !== CollapseItems.length - 1;
                return (
                  <Collapse.Panel
                    style={showBorder ? { borderBottom: '1px solid #D9D9D9' } : {}}
                    {...item}
                    forceRender
                  >
                    {item.children}
                  </Collapse.Panel>
                );
              })}
            </CustomCollapse>
          </div>
        </>
      </Form>
    </MarginWrapper>
  );
};

export default MetricForm;
